<template>
    <v-card variant="outlined" class="mb-3 pa-0">
        <v-card-text class="text-black d-flex flex-column">
            <v-row dense>
                <v-col cols="12" md="9">
                    <flight-itinerary
                        v-for="(itinerary, index) in offer.itinerary"
                        :key="index"
                        :itinerary="itinerary"
                    />
                    <flight-operating-airlines
                        :flight="offer"
                        class="d-flex text-caption justify-center justify-md-start ml-3"
                    />
                    <supplier-name-label v-if="isTO1" :supplier-name="offer.supplierName" class="mx-3 mb-2" />
                    <v-row v-if="!$breakpoint.smAndDown" no-gutters>
                        <v-col cols="12" md="12">
                            <v-btn variant="text" color="info" size="small" @click="expanded = !expanded">
                                {{ $t('flights.detail_info') }}
                            </v-btn>
                            <fare-rules :offer-key="offer.offerKey" />
                            <ProductOfferTipsDialog v-if="offer.tipsAvailable" :offer-key="offer.offerKey" />
                            <div class="ml-3">
                                <flight-baggage-allowances :offer="offer" />
                            </div>
                            <div class="ml-3">
                                <v-tooltip v-if="offer.longConnectionTime" location="top">
                                    <template v-slot:activator="{props}">
                                        <div class="mr-2 d-inline" v-bind="props">
                                            <v-icon color="primary" size="small">mdi-clock-outline</v-icon>
                                            <span class="text-grey">{{ $t('long_travel') }}</span>
                                        </div>
                                    </template>
                                    <span>{{ $t('connection_time_is_over_4_hours') }}</span>
                                </v-tooltip>
                                <v-tooltip v-if="offer.nightFlight" location="top">
                                    <template v-slot:activator="{props}">
                                        <div class="d-inline" v-bind="props">
                                            <v-icon size="small">mdi-weather-night</v-icon>
                                            <span class="text-grey">{{ $t('overnight') }}</span>
                                        </div>
                                    </template>
                                    <span>{{ $t('night_flight') }}</span>
                                </v-tooltip>
                            </div>
                        </v-col>
                    </v-row>
                </v-col>
                <v-col v-if="$breakpoint.smAndDown" cols="12" class="pa-0">
                    <v-row>
                        <v-col cols="12" md="3" class="py-1 py-md-3">
                            <v-btn variant="text" color="info" size="small" @click="expanded = !expanded">
                                {{ $t('flights.detail_info') }}
                            </v-btn>
                        </v-col>
                        <v-col cols="12" md="3" class="py-1 py-md-3">
                            <v-btn
                                v-if="offer.fareOffersArray || (offer.fareOptions && !offer.deltaCurrency)"
                                variant="text"
                                size="small"
                                color="info"
                                block
                                @click="showFareOptions = !showFareOptions"
                            >
                                {{ showFareOptions ? $t('flights.hide_tariffs') : $t('flights.show_tariffs') }}
                            </v-btn>
                            <fare-rules :offer-key="offer.offerKey" />
                            <ProductOfferTipsDialog v-if="offer.tipsAvailable" :offer-key="offer.offerKey" />
                        </v-col>
                        <v-col cols="12" class="py-1 pl-6">
                            <flight-baggage-allowances :offer="offer" />
                        </v-col>
                        <v-col v-if="offer.longConnectionTime || offer.nightFlight" cols="12" class="py-1 pl-6">
                            <v-tooltip v-if="offer.longConnectionTime" location="top">
                                <template v-slot:activator="{props}">
                                    <div class="mr-2 d-inline" v-bind="props">
                                        <v-icon color="primary" size="small">mdi-clock-outline</v-icon>
                                        <span class="text-grey">{{ $t('long_travel') }}</span>
                                    </div>
                                </template>
                                <span>{{ $t('connection_time_is_over_4_hours') }}</span>
                            </v-tooltip>
                            <v-tooltip v-if="offer.nightFlight" location="top">
                                <template v-slot:activator="{props}">
                                    <div class="d-inline" v-bind="props">
                                        <v-icon size="small">mdi-weather-night</v-icon>
                                        <span class="text-grey">{{ $t('overnight') }}</span>
                                    </div>
                                </template>
                                <span>{{ $t('night_flight') }}</span>
                            </v-tooltip>
                        </v-col>
                    </v-row>
                    <v-row v-if="$breakpoint.smAndDown" class="mb-4">
                        <v-col cols="12">
                            <v-expand-transition>
                                <keep-alive>
                                    <flight-detail v-if="expanded" :offer="offer" />
                                </keep-alive>
                            </v-expand-transition>
                            <v-expand-transition>
                                <keep-alive>
                                    <flight-fare-options v-if="showFareOptions" :offer="offer" />
                                </keep-alive>
                            </v-expand-transition>
                        </v-col>
                    </v-row>
                </v-col>
                <v-col
                    cols="12"
                    md="3"
                    class="d-flex justify-center align-center flex-wrap pa-0 pl-2 flex-column"
                    :class="{border: !$breakpoint.smAndDown}"
                >
                    <slot name="price">
                        <div v-if="tariffNameOptions" class="pa-0 text-subtitle-2">
                            <strong v-if="tarriffNameOptionsNames">
                                {{ tarriffNameOptionsNames }}
                            </strong>
                            <span v-if="tarriffNameOptionsNames" class="ml-1">/</span>
                            {{ Object.values(tariffNameOptions)[0].classes.join(' + ') }}
                        </div>
                        <template v-if="!offer.soldOut">
                            <supplier-price v-if="isTO1" :supplier-price="offer.supplierPrice" />
                            <div class="text-h5 d-flex align-center">
                                <div v-if="offer.price" class="text-right">
                                    <price-label
                                        :price="offer.price"
                                        :content-class="'text-h6 mb-1'"
                                        convert-price
                                        column
                                    />
                                    <discount-label :price="offer.price" class="d-inline-block mt-1" />
                                </div>
                                <corporate-policy-violation
                                    v-if="offer.corporatePolicyViolation"
                                    :policies="offer.corporatePolicy"
                                />
                                <price-details-btn class="text-body-1" :offer="offer" />
                            </div>
                            <commission-amount v-if="offer.price" :commission="offer.price.commission" />
                            <flight-conditions-label
                                class="mb-1"
                                small
                                :offer-key="offer.offerKey"
                                :price="offer.price"
                                :refundable="offer.refundable"
                                :regular="offer.regular"
                                @conditions="showConditionsInfo"
                            />
                        </template>
                        <div class="align-self-end widen">
                            <template v-if="!offer.soldOut">
                                <v-btn block color="success" class="mb-2" @click="selectForBooking">
                                    {{ $t('book') }}
                                </v-btn>
                                <v-btn
                                    v-if="offer.fareOffersArray || (offer.fareOptions && !offer.deltaPrice)"
                                    variant="text"
                                    size="small"
                                    color="info"
                                    block
                                    @click="showFareOptions = !showFareOptions"
                                >
                                    {{ showFareOptions ? $t('flights.hide_tariffs') : $t('flights.show_tariffs') }}
                                </v-btn>
                                <pricing-source-type-label :service="offer" class="text-center" />
                            </template>
                            <sold-out-modal v-else small />
                            <div v-if="offer.itinerary[0].segments[0].class?.quantity" class="text-center text-error">
                                {{
                                    $t('only_quantity_seats_are_available', {
                                        prop: offer.itinerary[0].segments[0].class.quantity,
                                    })
                                }}
                            </div>
                            <div v-else class="text-center">
                                <span v-if="offer.available" class="text-success">
                                    {{ $t('seats_are_available') }}
                                </span>
                                <span v-else class="text-warning">{{ $t('seats_are_available_on_request') }}</span>
                            </div>
                        </div>
                    </slot>
                </v-col>
            </v-row>
            <v-row v-if="!$breakpoint.smAndDown">
                <v-col cols="12">
                    <v-expand-transition>
                        <keep-alive>
                            <flight-detail v-if="expanded" :offer="offer" />
                        </keep-alive>
                    </v-expand-transition>
                    <v-expand-transition>
                        <keep-alive>
                            <flight-fare-options v-if="showFareOptions" :offer="offer" />
                        </keep-alive>
                    </v-expand-transition>
                </v-col>
            </v-row>
        </v-card-text>
    </v-card>
</template>

<script lang="ts">
    import {Component, Prop, Vue} from 'vue-facing-decorator'
    import FlightItinerary from '@/components/flights/snippets/FlightItinerary.vue'
    import PriceDetailsBtn from '~/components/flights/PriceDetailsBtn.vue'
    import FlightConditionsLabel from '@/components/flights/snippets/FlightConditionsLabel.vue'
    import DiscountLabel from '~src/components/snippets/discountLabel.src'
    import {EventBus, SHOW_FLIGHT_CONDITIONS_INFO} from '@/utils/event-bus'
    import PriceLabel from '~src/components/snippets/priceLabel.src'
    import SupplierPrice from '~src/components/snippets/supplierPrice.src'
    import FlightOperatingAirlines from '@/components/flights/snippets/FlightOperatingAirlines.vue'
    import SupplierNameLabel from '@/components/hotels/snippets/SupplierNameLabel.vue'
    import PricingSourceTypeLabel from '@/components/snippets/PricingSourceTypeLabel.vue'
    import FlightDetail from '~/components/flights/search/offers/FlightDetail.vue'
    import FlightFareOptions from '~/components/flights/search/offers/FlightFareOptions.vue'
    import FlightBaggageAllowances from '~/components/flights/search/offers/FlightBaggageAllowances.vue'
    import CommissionAmount from '~/components/snippets/CommissionAmount.vue'
    import CorporatePolicyViolation from '~/components/snippets/CorporatePolicyViolation.vue'
    import FareRules from '~/components/flights/FareRules.vue'
    import ProductOfferTipsDialog from '~/components/modals/ProductOfferTipsDialog.vue'
    import SoldOutModal from '~src/components/snippets/SoldOutModal.vue'
    import {chunkArray} from '~/utils/helpers'

    @Component({
        components: {
            SoldOutModal,
            ProductOfferTipsDialog,
            PricingSourceTypeLabel,
            FlightOperatingAirlines,
            PriceLabel,
            FlightConditionsLabel,
            FlightItinerary,
            FlightDetail,
            FlightFareOptions,
            CommissionAmount,
            PriceDetailsBtn,
            CorporatePolicyViolation,
            FareRules,
            DiscountLabel,
            SupplierPrice,
            SupplierNameLabel,
            FlightBaggageAllowances,
        },
    })
    export class FlightCard extends Vue {
        @Prop({required: true}) offer

        expanded = false
        showFareOptions = false

        selectForBooking() {
            flightsStore.ADD_TO_BASKET(this.offer)
            this.$router.push({
                name: 'flightBooking',
                query: {offerKey: this.offer.offerKey},
            })
        }

        showConditionsInfo() {
            EventBus.$emit(SHOW_FLIGHT_CONDITIONS_INFO, this.offer)
        }

        get baggageAllowances() {
            if (!this.offer.itinerary[0].segments[0]?.baggageInfo?.length) return {label: 'is_unavailable'}

            const baggage = this.offer.itinerary[0].segments[0].baggageInfo[0].baggage
            const unitQuantity = +baggage.unitQuantity
            const unitCode = baggage.unitCode
            const partlyAvailable = this.offer.itinerary.some(({segments}) =>
                segments.some(
                    ({baggageInfo}) =>
                        baggageInfo &&
                        +baggageInfo[0].baggage.unitQuantity !== unitQuantity &&
                        baggageInfo[0].baggage.unitCode !== unitCode
                )
            )

            if (partlyAvailable) return {label: 'partly_available'}

            return {
                unitQuantity,
                unitCode,
                label: unitQuantity > 0 ? 'included' : 'not_included',
            }
        }

        get isTO1() {
            return authStore.isTO1
        }

        get tariffNameOptions() {
            const offerTariff = {}
            this.offer.itinerary.forEach(({segments}) => {
                segments.forEach((seg, idx) => {
                    const brandedFareName = seg.brandedFareName ?? idx
                    const categoryClass = seg.class

                    if (!offerTariff[brandedFareName]) {
                        offerTariff[brandedFareName] = {
                            name: Number.isFinite(brandedFareName) ? '' : brandedFareName,
                            classes: [],
                        }
                        if (categoryClass && categoryClass.classType) {
                            offerTariff[brandedFareName].classes.push(categoryClass.classType)
                        }
                    }
                })
            })
            return isEmpty(offerTariff) ? null : offerTariff
        }

        get tarriffNameOptionsNames() {
            let result = []
            Object.keys(this.tariffNameOptions).forEach(key => {
                if (!this.tariffNameOptions[key].name) return
                result.push(this.tariffNameOptions[key].name)
            })

            return result.length ? result.join(' + ') : null
        }
    }

    export default FlightCard
</script>

<style lang="scss" scoped>
    .border {
        border-left: 1px solid grey !important;
    }

    .widen {
        width: 100%;
    }
</style>
